/**
 * "Nytt från bokningsmail" – tolkning, matchning och kallelse.
 *
 * Ren logik utan Firestore. Formatet är Comdevas bokningsmail: etikett på en
 * rad, värde på nästa. Tidigare kopior av tolkningen i CRMLeads/CRMLead/
 * CRMCompanies/CRMCompany används fortfarande av de gamla dialogerna och är
 * orörda; den här modulen används av det nya flödet.
 */
import { buildFlatboxBody } from '../../utils/outlookMeeting';
import { orgNrSiffror, domanFranEpost, arForetagsdoman } from './kundmatchning';

// Kundmatchningen ligger i kundmatchning.js (utan importer, så att även
// Node-skript kan använda den) och exporteras vidare härifrån.
export {
  GENERISKA_DOMANER, orgNrSiffror, normaliseraForetagsnamn, domanFranEpost, arForetagsdoman,
  domanFranWebbplats, matchaKund,
} from './kundmatchning';

// ── Konstanter ──────────────────────────────────────────────────────────────

export const MOTE_STANDARD_MINUTER = 30;

export const LEAD_KALLA_COMDEVA = 'Comdeva';

// ── Hjälpfunktioner ─────────────────────────────────────────────────────────

const pad = n => String(n).padStart(2, '0');

/** Svenska organisationsnummer: Luhn på tio siffror. */
export function luhnOk(d) {
  if (!/^\d{10}$/.test(d)) return false;
  let sum = 0;
  for (let i = 0; i < 10; i++) {
    let n = Number(d[i]);
    if (i % 2 === 0) n *= 2;
    if (n > 9) n -= 9;
    sum += n;
  }
  return sum % 10 === 0;
}

/** Giltigt org.nr (tio siffror med rätt kontrollsiffra), annars ''. */
export function giltigtOrgNr(value) {
  const d = orgNrSiffror(value);
  return d && luhnOk(d) ? d : '';
}

export function formateraOrgNr(d) {
  return d && d.length === 10 ? `${d.slice(0, 6)}-${d.slice(6)}` : d || '';
}

// ── Tolkning ────────────────────────────────────────────────────────────────

const ETIKETTER = {
  'start': 'start',
  'slut': 'slut',
  'företag/person': 'foretag',
  'kundnummer': 'kundnummer',
  'ort': 'ort',
  'antal anställda': 'anstallda',
  'telefonnr': 'telefon',
  'telefon': 'telefon',
  'telefonnummer': 'telefon',
  'bransch': 'bransch',
  'omsättning': 'omsattning',
  'plats': 'plats',
  'ämne': 'amne',
  'beskrivning': 'beskrivning',
  'resurs': 'resurs',
};

const ar_mokommentar = rad => /^möteskommentar/i.test(rad);

function etikett(rad) {
  return ETIKETTER[rad.toLowerCase().replace(/:$/, '').trim()] || null;
}

/** `[x](mailto:x)` → `x`, `Namn <x>` → `Namn x`, `mailto:x` → `x`. */
function stadaRad(rad) {
  return rad
    .replace(/\[([^\]]*)\]\(mailto:([^)]+)\)/gi, (_, text, adr) => (text.includes('@') ? text : adr))
    .replace(/<\s*(?:mailto:)?([^<>\s]+@[^<>\s]+)\s*>/gi, ' $1')
    .replace(/\bmailto:/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const EPOST_RE = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/;
const TELEFON_RE = /^\+?[\d\s()-]{7,}$/;

const MANADER = {
  januari: 1, februari: 2, mars: 3, april: 4, maj: 5, juni: 6,
  juli: 7, augusti: 8, september: 9, oktober: 10, november: 11, december: 12,
};

/**
 * "2026-10-28 13:30 On" → { datum: '2026-10-28', tid: '13:30' }.
 * Tål skräp efter tiden och svensk datumform ("28 oktober 2026 13:30").
 */
export function tolkaTid(text) {
  const s = String(text || '');
  let datum = '';
  const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (iso) datum = `${iso[1]}-${iso[2]}-${iso[3]}`;
  else {
    const sv = /(\d{1,2})\s+([a-zåäö]+)\s+(\d{4})/i.exec(s);
    const man = sv && MANADER[sv[2].toLowerCase()];
    if (man) datum = `${sv[3]}-${pad(man)}-${pad(sv[1])}`;
  }
  const t = /(?:^|\D)([01]?\d|2[0-3])[:.]([0-5]\d)(?!\d)/.exec(datum ? s.replace(/\d{4}-\d{2}-\d{2}/, ' ') : s);
  return { datum, tid: t ? `${pad(t[1])}:${t[2]}` : '' };
}

/** 'YYYY-MM-DDTHH:mm' plus minuter (kalenderaritmetik, ingen tidszon). */
export function laggTillMinuter(lokal, minuter) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(lokal || '');
  if (!m) return '';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5] + minuter));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/**
 * Tolkar ett bokningsmail. Returnerar null om texten inte ser ut som ett.
 *
 * mote.start / mote.slut är lokal tid i Europe/Stockholm som 'YYYY-MM-DDTHH:mm'.
 */
export function tolkaBokningsmail(text) {
  if (!text || !text.trim()) return null;
  const rader = text.split(/\r?\n/).map(stadaRad);
  if (!rader.some(r => ['foretag', 'kundnummer', 'beskrivning'].includes(etikett(r)))) return null;

  const varden = {};
  const beskrivning = [];
  let anteckning = null;

  for (let i = 0; i < rader.length; i++) {
    const rad = rader[i];
    if (anteckning !== null) { anteckning.push(rad); continue; }
    if (ar_mokommentar(rad)) { anteckning = []; continue; }
    const nyckel = etikett(rad);
    if (!nyckel) continue;

    if (nyckel === 'beskrivning') {
      let j = i + 1;
      for (; j < rader.length; j++) {
        const r = rader[j];
        if (!r) continue;
        if (etikett(r) || ar_mokommentar(r)) break;
        beskrivning.push(r);
      }
      i = j - 1;
      continue;
    }

    // Värdet är nästa rad som inte är tom och inte själv är en etikett.
    let j = i + 1;
    while (j < rader.length && !rader[j]) j++;
    if (j < rader.length && !etikett(rader[j]) && !ar_mokommentar(rader[j])) {
      if (!(nyckel in varden)) varden[nyckel] = rader[j];
      i = j;
    }
  }

  // Kontakt ur Beskrivning: rad 1 titel, rad 2 namn, sedan telefon och e-post i valfri ordning.
  let epost = '';
  let mobil = '';
  const text_rader = [];
  for (const r of beskrivning) {
    const e = EPOST_RE.exec(r);
    if (e && !epost) { epost = e[0].toLowerCase(); continue; }
    if (TELEFON_RE.test(r) && !mobil) { mobil = r; continue; }
    text_rader.push(r);
  }
  let titel = '';
  let namn = '';
  if (text_rader.length >= 2) [titel, namn] = text_rader;
  else if (text_rader.length === 1) [namn] = text_rader;
  const [fornamn = '', ...rest] = namn.split(' ').filter(Boolean);

  // Tider
  const start = tolkaTid(varden.start);
  const slutT = tolkaTid(varden.slut);
  const startLokal = start.datum && start.tid ? `${start.datum}T${start.tid}` : '';
  const slutLokal = slutT.tid
    ? `${slutT.datum || start.datum}T${slutT.tid}`
    : laggTillMinuter(startLokal, MOTE_STANDARD_MINUTER);

  const kundnummer = (varden.kundnummer || '').trim();
  const doman = domanFranEpost(epost);

  return {
    kund: {
      namn: varden.foretag || '',
      comdevaKundnummer: kundnummer,
      orgNr: giltigtOrgNr(kundnummer),
      ort: varden.ort || '',
      antalAnstallda: varden.anstallda || '',
      telefon: varden.telefon || '',
      bransch: varden.bransch || '',
      omsattning: varden.omsattning || '',
    },
    kontakt: { titel, fornamn, efternamn: rest.join(' '), mobil, epost },
    mote: {
      start: startLokal,
      slut: slutLokal,
      plats: varden.plats || '',
      amne: varden.amne || '',
    },
    internAnteckning: (anteckning || []).join('\n').replace(/\n{3,}/g, '\n\n').trim(),
    doman: arForetagsdoman(doman) ? doman : '',
  };
}

// ── Matchning ───────────────────────────────────────────────────────────────
// Kund: matchaKund() i kundmatchning.js.

/** Kontakt på e-post, bara bland den matchade kundens kontakter. */
export function matchaKontakt(epost, kontakter) {
  const e = String(epost || '').trim().toLowerCase();
  if (!e) return null;
  return kontakter.find(c => String(c.email || '').trim().toLowerCase() === e) || null;
}

/** Öppna leads på kunden: allt utom Vunnen och Förlorad. */
export function oppnaLeads(companyId, leads) {
  if (!companyId) return [];
  return leads.filter(l => l.companyId === companyId && !['won', 'lost'].includes(l.status));
}

// ── Kallelse ────────────────────────────────────────────────────────────────

/**
 * Lokal tid i Europe/Stockholm ('YYYY-MM-DDTHH:mm') → Date. Tar hänsyn till
 * sommartid för just det datumet, oavsett vilken tidszon datorn står i.
 */
export function stockholmTillDate(lokal) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(lokal || '');
  if (!m) return null;
  const somUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const offset = t => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Stockholm', hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).formatToParts(new Date(t)).map(x => [x.type, x.value]));
    return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - t;
  };
  let t = somUtc - offset(somUtc);
  t = somUtc - offset(t);
  return new Date(t);
}

/** ISO med tidszon i UTC: '2026-10-28T12:30:00Z'. */
export function isoUtc(lokal) {
  const d = stockholmTillDate(lokal);
  return d ? d.toISOString().replace(/\.\d{3}Z$/, 'Z') : '';
}

/**
 * Outlook på webben med ett nytt möte förifyllt. `to` lägger till deltagare –
 * parametern är inte dokumenterad av Microsoft och måste provas (testlistan).
 */
export function outlookDeeplink({ amne, start, slut, plats, text, epost }) {
  const p = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: amne || '',
    startdt: isoUtc(start),
    enddt: isoUtc(slut),
    location: plats || '',
    body: text || '',
  });
  if (epost) p.set('to', epost);
  return `https://outlook.office.com/calendar/0/deeplink/compose?${p.toString()}`;
}

/** 'YYYY-MM-DDTHH:mm' → '20261028T133000' (lokal tid för TZID-rader i ICS). */
export function icsLokal(lokal) {
  return (lokal || '').replace(/[-:]/g, '') + '00';
}

/**
 * Mötesobjekt i det format buildIcs (utils/outlookMeeting.js) tar emot.
 * Brödtexten byggs som i "Boka möte" från lead: fetstilt tagline och
 * avsändarens signatur sist.
 */
export function moteForIcs({
  amne, start, slut, plats, text, kundnamn, kontaktnamn, epost, signatureHtml = '', signatureText = '',
}) {
  return {
    subject: amne || '',
    customerName: kundnamn || '',
    date: (start || '').slice(0, 10),
    ...buildFlatboxBody({ text: text || '', signatureHtml, signatureText }),
    location: plats || '',
    icsStart: icsLokal(start),
    icsEnd: icsLokal(slut),
    attendees: epost ? [{ name: kontaktnamn || epost, email: epost }] : [],
  };
}

// ── Allabolag och LinkedIn ──────────────────────────────────────────────────

/** Sökning på allabolag.se (verifierad 2026-10-19: listar träffar på namnet). */
export function allabolagSokUrl(namn) {
  return `https://www.allabolag.se/bransch-s%C3%B6k?q=${encodeURIComponent(namn || '')}`;
}

export function linkedinForetagSokUrl(namn) {
  return `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(namn || '')}`;
}

export function linkedinPersonSokUrl(namn, foretag) {
  const ord = [namn, foretag].filter(Boolean).join(' ');
  return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(ord)}`;
}

/** Giltig LinkedIn-adress för typen: '/company/…' eller '/in/…'. */
export function giltigLinkedinUrl(url, typ) {
  const s = String(url || '').trim();
  const del = typ === 'company' ? 'company' : 'in';
  return new RegExp(`^(https?://)?([a-z]{2,3}\\.)?linkedin\\.com/${del}/[^/?#\\s]+`, 'i').test(s);
}
